import { push } from '@lagunovsky/redux-react-router'
import { chunk, compact, groupBy, keys, maxBy, minBy, orderBy, range, take } from 'lodash'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import { H_STATION_QUALITO_STATES } from '../../../account/constants/AccessRulesConstants'
import NumberField from '../../../components/forms/NumberField'
import RadioButtons from '../../../components/forms/RadioButtons'
import ExportFileModal from '../../../components/modal/ExportFileModal'
import ExportAction from '../../../export/actions/ExportAction'
import { nbPerPageLabel } from '../../../referencial/constants/ReferencialConstants'
import { getStationArrowNav } from '../../../utils/ActionUtils'
import { exportModelFile, formatData, getModelFileType } from '../../../utils/ExportDataUtil'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { hasValue } from '../../../utils/NumberUtil'
import { CHIM_STATE, getStateLabels, GOOD } from '../../../utils/QualityUtils'
import { getLinks, getStationTitleWithTownName } from '../../../utils/StationUtils'
import QualityAction from '../../actions/QualityAction'
import QualitometerStateModal from './QualitometerStateModal'
import { NewTable } from 'components/datatable/NewTable'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { useParams } from 'react-router'
import { NoDataCard } from 'components/card/NoDataCard'

const QualitometerStatesApp = () => {
    const dispatch = useDispatch()

    const { id } = useParams()

    const {
        qualitometer,
        qualitometers,
        qualitometerStates,
        qualityStates,
        typeEnvironmentModels,
        accountUser,
        stationWatermass,
        citiesIndex,
    } = useSelector(store => ({
        qualitometer: store.QualityReducer.qualitometer,
        qualitometers: store.QualityReducer.qualitometersLight,
        qualitometerStates: store.QualityReducer.qualitometerStates,
        qualityStates: store.QualityReducer.qualityStates,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
        accountUser: store.AccountReducer.accountUser,
        stationWatermass: store.StationReducer.stationWatermass,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const [startYear, setStartYear] = useState(moment().year())
    const [endYear, setEndYear] = useState(moment().year())
    const [isOpen, setIsOpen] = useState(false)
    const [openExportModal, setOpenExportModal] = useState(false)
    const [yearChoice, setYearChoice] = useState(1)
    const [readMode, setReadMode] = useState(true)
    const [qualitometerState, setQualitometerState] = useState({ })
    const [isNew, setIsNew] = useState(false)

    useEffect(() => {
        setStartYear(moment(minBy(qualitometerStates, 'startDate')?.startDate).year())
    }, [qualitometerStates])

    useEffect(() => {
        if (!componentHasHabilitations(H_STATION_QUALITO_STATES)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return
        }

        dispatch(QualityAction.fetchQualitometerStates(id)).then((states) => {
            const date = minBy(states, 'startDate')
            setStartYear(moment(date?.startDate).year())
        })

        if (!qualityStates.length) {
            dispatch(QualityAction.fetchQualityStates())
        }
    }, [])

    const townName = useMemo(() => citiesIndex[qualitometer.townCode]?.name, [citiesIndex, qualitometer])

    useTitle(() => {
        return [{
            title: i18n.quality,
            href: 'quality',
        }, {
            title: getStationTitleWithTownName(qualitometer, townName),
            href: `station/quality/${id}`,
        }, {
            title: i18n.states,
            href: `station/quality/${id}'/qualityStates'`,
        }]
    }, [qualitometer, id, townName])

    const setStateModal = (qualitoState = {}, isNewValue = false) => {
        setIsOpen(true)
        setIsNew(isNewValue)
        setQualitometerState(qualitoState)
    }

    useActions(() => {
        const edit = accountUser.isAdmin === '1' ? { edit: () => setReadMode(false) } : {}
        return readMode ? {
            ...edit,
            exportList: [{
                onClick: () => setOpenExportModal(true),
                label: i18n.excel,
            }],
            links: getLinks(qualitometer, { stationWatermass }),
            arrowNav: getStationArrowNav('quality', qualitometers, qualitometer.id, s => dispatch(push(`/station/quality/${s.id}/qualityStates`))),
        } : {
            cancel: () => setReadMode(true),
            exportList: [{
                onClick: () => setOpenExportModal(true),
                label: i18n.excel,
            }],
            new: () => setStateModal({}, true),
            links: getLinks(qualitometer, { stationWatermass }),
            arrowNav: getStationArrowNav('quality', qualitometers, qualitometer.id, s => push(`/station/quality/${s.id}/qualityStates`)),
        }
    }, [qualitometers, qualitometer, readMode])

    const getStateYear = (s, allYears) => {
        if (yearChoice == 1) {
            return moment(s.startDate).year()
        }
        return allYears.find(y => y.includes(moment(s.startDate).year().toString().slice(2)))
    }

    const filterStates = qualitometerStates.filter(s => hasValue(s.startDate) && hasValue(s.value)).filter(s => {
        const year = moment(s.startDate).year()
        return year >= startYear && year <= endYear
    })

    const allYears = useMemo(() => {
        const years = take(range(endYear, startYear -1), 100).map(d => `${d}`)
        if (yearChoice == 1) {
            return years
        }
        return chunk(years, yearChoice).map(group => group.map(y => y.toString().slice(2)).join('-'))
    }, [endYear, startYear, yearChoice])

    const stateGroup = groupBy(filterStates, 'stateId')
    const stateLabels = getStateLabels()

    const checkPrincipalIsEmpty = orderBy(filterStates, 'order').reduce((acc, val) => {
        if (val.principal === '1') {
            return { ...acc, order: val.order, res: { ...acc.res, [val.order]: true } }
        }
        return { ...acc, res: { ...acc.res, [acc.order]: false } }
    }, { order: -1, res: {} })

    const tableData = compact(orderBy(keys(stateGroup).map(key => {
        const states = stateGroup[key]
        const currentState = states[0]
        if (checkPrincipalIsEmpty.res[currentState.order]) return undefined

        const stateByYearGroups = groupBy(states, s => getStateYear(s, allYears))
        const years = keys(stateByYearGroups).reduce((acc, val) => {
            const yearStates = stateByYearGroups[val]
            const worst = maxBy(yearStates, 'value')
            const stateLabel = (worst.stateId === CHIM_STATE && worst.value === GOOD) // on chimical State there's only good and bad
                ? { ...stateLabels[worst.value], color: 'blue' }
                : (stateLabels[worst.value] || { color: 'white' })
            return ({
                ...acc,
                [val]: {
                    sortValue: worst.value,
                    ...(worst.principal === '1' ? {} : stateLabel),
                    tooltip: worst.comment,
                    className: worst.comment && 'tooltipped clickable',
                    onClick: () => setStateModal(worst),
                },
            })
        }, {})
        return {
            state: {
                value: <span style={currentState.principal === '1' ? { fontWeight: 'bold' } : { paddingLeft: '2rem' } }>{currentState.stateName}</span>,
                sortValue: currentState.stateName,
            },
            order: currentState.order,
            ...years,
        }
    }), 'order'))
    const headers = ['state', ...allYears]

    const exportData = (type, title) => {
        if (!tableData.length) {
            return dispatch(ToastrAction.error(i18n.noDataToExport))
        }
        const formattedData = formatData(tableData.map((d, i) => i !== 0 ? d : { ...d, headers }))
        return dispatch(ExportAction.standardExport(formattedData, type, title))
    }

    const getExportModal = () => {
        const tableExport = [{
            name: i18n.resultsTable,
            formats: [{
                type: i18n.excelFile,
                callback: () => exportData('xlsx', `${qualitometer.code} - ${i18n.states}`),
            }, {
                type: i18n.csvFile,
                callback: () => exportData('csv', `${qualitometer.code} - ${i18n.states}`),
            }],
        }]
        const exportModel = typeEnvironmentModels.map((model) => {
            const fileNameSplit = model.split('.')
            const name = fileNameSplit.slice(0, -1).join('')
            const ext = fileNameSplit[fileNameSplit.length - 1]
            return {
                name,
                formats: [{
                    type: getModelFileType(ext),
                    callback: () => exportModelFile({
                        stationId: qualitometer.id.toString(),
                        stationCode: qualitometer.code,
                        stationType: qualitometer.typeName,
                        environmentModels: typeEnvironmentModels,
                        filenameModelExport: model,
                    }),
                }],
            }
        })
        return openExportModal && (
            <ExportFileModal
                open={openExportModal}
                onClose={() => setOpenExportModal(false) }
                data={[ ...tableExport, ...exportModel ]}
            />
        )
    }

    const yearChoices = [
        { code: 1, name: i18n.year },
        { code: 2, name: `${2} ${i18n.nbYears}` },
        { code: 3, name: `${3} ${i18n.nbYears}` },
    ]

    return (
        <>
            <Card sx={{ margin: '10px 10px 0 20px' }}>
                <CardContent>
                    <Stack spacing={2}>
                        <Grid container spacing={2}>
                            <Grid size={2}>
                                <NumberField
                                    title={i18n.startYear}
                                    value={startYear}
                                    onChange={v => setStartYear(v ? v : moment().year() - 3) }
                                />

                            </Grid>
                            <Grid size={2}>
                                <NumberField
                                    title={i18n.endYear}
                                    value={endYear}
                                    onChange={v => setEndYear(v ? v : moment().year())}
                                />
                            </Grid>
                            <Grid size={5}>
                                <RadioButtons
                                    elements={yearChoices}
                                    title={i18n.regrouping}
                                    selected={yearChoice}
                                    onChange={v => setYearChoice(v) }
                                />
                            </Grid>
                        </Grid>
                        {tableData.length ? (
                            <NewTable
                                rows={tableData}
                                headers={headers}
                                rowsPerPageOptions={nbPerPageLabel}
                            />
                        ) : <NoDataCard />}
                    </Stack>
                </CardContent>
            </Card>
            {openExportModal && getExportModal()}
            <QualitometerStateModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                isNew={isNew}
                qualitometerState={qualitometerState}
                readMode={readMode}
            />
        </>
    )
}

export default QualitometerStatesApp